import React, { useState, useMemo, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Fade,
  Grid2,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { FilterAlt, Replay } from "@mui/icons-material";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import { StartDatePicker } from "../DateSelector/DatePickers/StartDatePicker";
import { EndDatePicker } from "../DateSelector/DatePickers/EndDatePicker";
import { FleetFilterSlideout } from "./FleetFilterSlideout";
import { AreaChartsReport } from "../FacilityReport/area-chart.component";
import WeekdayBars from "../FacilityReport/weekday-report-chart.component";
import CapacityPerHourReport from "../FacilityReport/capacity-per-hour-report.component";
import { FleetCapacityReportTable } from "./FleetCapacityReportTable";
import { initialWeekdays } from "../../../features/Reports/initialWeekdays.constant";
import { ReportEntities, TimeframeString } from "../../../features/Reports/typings/reports.types";
import { FleetFilterApplyPayload } from "../../../features/Reports/typings/fleet-capacity-report.types";
import {
  setCapacityDataEmpty,
  setCapacitySelectedFloors,
  setCurrentFloors,
  setSelectableEntitiesReplace
} from "../../../features/Reports/slices/report.slice";
import { useRemoteFetchAdminLocations } from "../../../hooks/Remote/Location/useRemoteFetchAdminLocations";
import { useFleetCapacityReportMutations } from "../../../hooks/Remote/Fleet/Report/useFleetCapacityReportMutations";
import {
  formatCapacityPercent,
  mapEntitiesVehiclesToFleetRows,
  mapFleetCapacityToCapacityObjects,
  mapFleetCapacityToEntityData
} from "./fleet-capacity-report.functions";
import DefaultSpinner from "../../LoadingSpinner/default-spinner.component";
import { SvgReportChipGroup } from "../../Svg/SvgReportChipGroup/SvgReportChipGroup.component";
import { areDaysEqual } from "../CapacityFilter/DateAndTypeSelector/functions/areWeekdaysEqual.function";

const defaultStart = DateTime.local().minus({ months: 1 }).toFormat("yyyy-MM-dd");
const defaultEnd = DateTime.local().toFormat("yyyy-MM-dd");
const initialTimeframeStart = DateTime.local().minus({ year: 1 }).toFormat("yyyy-MM-dd");

/**
 * Fleet Capacity Report page (full page, not dialog).
 *
 * Layout: date range, filter button (slideout: weekdays + departments), Calculate.
 * After Calculate: vehicle table (all vehicles in date range from entities), KPI chips, area charts.
 *
 * Data flow:
 * - Date/timeframe change + Calculate: full fetch (entities + timeframe + capacity). Entities define all vehicles in range.
 * - Filter slideout Apply (departments, weekdays): keep entities, fetch capacity only.
 * - Vehicle selection: updates selection only; user clicks Calculate to refetch capacity for the new selection.
 *
 * @see docs/features/fleets/FE/Fleet-Capacity-Report-Data-Flow.md — API, params, end-to-end flow, filtering.
 */
export function FleetCapacityReportPage(): React.ReactElement {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const dispatch = useDispatch();
  const {
    settings: { timezone }
  } = useSelector((state: RootState) => state.login);

  const { workplaceBookingEnabled } = useSelector(
    (state: RootState) => state.login.userInformation.company.meta
  );

  const [selectedTimeframe, setSelectedTimeframe] = useState<TimeframeString>({
    timeframeStart: defaultStart,
    timeframeEnd: defaultEnd
  });

  const [filterSlideOpen, setFilterSlideOpen] = useState(false);

  const [selectedVehicleIds, setSelectedVehicleIds] = useState<number[]>([]);
  /** Vehicle IDs from the last full capacity report fetch; used to show reset when selection differs. */
  const [initialReportVehicleIds, setInitialReportVehicleIds] = useState<number[]>([]);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<string[]>([]);
  const [selectedWeekdays, setSelectedWeekdays] = useState(initialWeekdays);

  const [fetchEnabled, setFetchEnabled] = useState(true);

  const { data: locations } = useRemoteFetchAdminLocations({ mode: "admin" });

  const {
    entitiesData,
    timeframeData,
    capacityData,
    fetchFleetCapacityReport,
    fetchCapacityOnly,
    isReportFetching
  } = useFleetCapacityReportMutations();

  const initialTimeframe = useMemo(() => {
    // initially timeframe is set to 1 year ago to today for minimum value of start date picker
    if (!timeframeData) return { timeframeStart: initialTimeframeStart, timeframeEnd: "" };

    const { startDate, endDate } = timeframeData;
    setSelectedTimeframe({ timeframeStart: startDate, timeframeEnd: endDate });
    return { timeframeStart: startDate, timeframeEnd: endDate };
  }, [timeframeData]);

  const fleetRows = useMemo(() => {
    if (!entitiesData?.vehicles || !locations) return undefined;
    return mapEntitiesVehiclesToFleetRows(entitiesData.vehicles, locations);
  }, [entitiesData?.vehicles, locations]);

  const { capacityObj, capacityEntityData } = useMemo(
    () => ({
      capacityObj: mapFleetCapacityToCapacityObjects(capacityData, t("Drawer_Fleet")),
      capacityEntityData: mapFleetCapacityToEntityData(capacityData)
    }),
    [capacityData, t]
  );

  const isFilterApplied = useMemo(() => {
    return !areDaysEqual(selectedWeekdays, initialWeekdays) || selectedDepartmentIds.length > 0;
  }, [selectedWeekdays, selectedDepartmentIds]);

  const isInitialReportState = useMemo(() => {
    if (!capacityData?.uniqueInventoryIds) return true;

    const reportIds = capacityData.uniqueInventoryIds.slice().sort((a, b) => a - b);
    const selected = selectedVehicleIds.slice().sort((a, b) => a - b);

    if (reportIds.length !== selected.length) return false;

    return reportIds.every((id, i) => id === selected[i]);
  }, [capacityData?.uniqueInventoryIds, selectedVehicleIds]);

  /** True when current selection differs from the initial set stored after the last full fetch. */
  const selectionDiffersFromInitial = useMemo(() => {
    const initial = initialReportVehicleIds.slice().sort((a, b) => a - b);
    const selected = selectedVehicleIds.slice().sort((a, b) => a - b);
    if (initial.length !== selected.length) return true;
    return !initial.every((id, i) => id === selected[i]);
  }, [initialReportVehicleIds, selectedVehicleIds]);

  const showResetButton =
    Boolean(capacityData) &&
    (!areDaysEqual(selectedWeekdays, initialWeekdays) ||
      selectedDepartmentIds.length > 0 ||
      !isInitialReportState ||
      selectionDiffersFromInitial);

  const baseReportParams = {
    timezone,
    calcPerDay: true,
    calcPerHour: true,
    calcPerWeekday: true,
    calcPerWorkplace: true
  };

  const reportedEntityNames = useMemo(() => {
    const vehicles = entitiesData?.vehicles ?? [];
    const filtered = vehicles.filter(
      (v): v is typeof v & { vehicleInventoryId: number } =>
        v.vehicleInventoryId !== undefined && selectedVehicleIds.includes(v.vehicleInventoryId)
    );
    if (!filtered.length) return t("Drawer_Fleet");

    const names = filtered.map(v => v.name);

    return names.length > 4 ? names.slice(0, 4).join(", ") + ", ..." : names.join(", ");
  }, [entitiesData?.vehicles, selectedVehicleIds, t]);

  const FLEET_REPORT_ENTITY: ReportEntities = useMemo(
    () => ({
      id: 0,
      locations: [],
      floors: [{ name: reportedEntityNames, inventoryId: 0 }],
      zones: [],
      places: []
    }),
    [reportedEntityNames]
  );

  const isTimeframeUnchanged =
    timeframeData &&
    selectedTimeframe.timeframeStart === timeframeData.startDate &&
    selectedTimeframe.timeframeEnd === timeframeData.endDate;

  const handleReset = () => {
    setSelectedDepartmentIds([]);
    setSelectedWeekdays(initialWeekdays);

    fetchFleetCapacityReport({
      start: selectedTimeframe.timeframeStart,
      end: selectedTimeframe.timeframeEnd,
      vehicleIds: [],
      filter: [],
      departmentIds: [],
      weekdays: initialWeekdays,
      ...baseReportParams
    }).then(data => {
      const ids = data?.capacityPerformancePerWorkplace.map(w => w.bookingObject) ?? [];
      setFilterSlideOpen(false);
      setFetchEnabled(false);
      setSelectedVehicleIds(ids);
      setInitialReportVehicleIds(ids);
    });
  };

  const handleCalculate = async () => {
    const capacityParams = {
      start: selectedTimeframe.timeframeStart,
      end: selectedTimeframe.timeframeEnd,
      filter: selectedVehicleIds,
      departmentIds: selectedDepartmentIds,
      weekdays: selectedWeekdays,
      ...baseReportParams
    };

    if (isTimeframeUnchanged && entitiesData?.vehicles?.length) {
      return await fetchCapacityOnly(capacityParams);
    } else {
      const data = await fetchFleetCapacityReport({
        ...capacityParams,
        vehicleIds: selectedVehicleIds
      });
      const ids = data?.capacityPerformancePerWorkplace.map(w => w.bookingObject) ?? [];
      setSelectedVehicleIds(ids);
      setInitialReportVehicleIds(ids);
      return data;
    }
  };

  const handleFilterCalculate = (payload: FleetFilterApplyPayload, isReset: boolean) => {
    if (isReset) return handleReset();

    const { departmentIds, weekdays } = payload;

    setSelectedDepartmentIds(departmentIds);
    setSelectedWeekdays(weekdays);

    fetchCapacityOnly({
      start: selectedTimeframe.timeframeStart,
      end: selectedTimeframe.timeframeEnd,
      filter: selectedVehicleIds,
      departmentIds,
      weekdays,
      ...baseReportParams
    }).then(() => {
      setFilterSlideOpen(false);
      setFetchEnabled(false);
    });
  };

  useEffect(() => {
    if (!isReportFetching && fleetRows?.length && capacityData) {
      setFetchEnabled(false);
      dispatch(setCapacityDataEmpty(true));
      dispatch(setCurrentFloors([{ id: 0, open: true }]));
      dispatch(setSelectableEntitiesReplace([FLEET_REPORT_ENTITY]));
      dispatch(setCapacitySelectedFloors({ id: 0, capacity: capacityData }));
    } else {
      dispatch(setCapacityDataEmpty(true));
    }
  }, [isReportFetching, fleetRows, capacityData, dispatch]);

  return (
    <>
      <Box sx={{ width: "100%", p: 2 }} data-testid="fleet-capacity-report-parent">
        <Grid2
          container
          data-testid="fleet-report-filter-header"
          alignItems="center"
          sx={{ flexWrap: "wrap", gap: 2, mb: 2 }}
        >
          <Grid2 container alignItems="center" sx={{ gap: 1, flexWrap: "wrap" }}>
            <Grid2>
              <StartDatePicker
                selectedTimeframe={selectedTimeframe}
                initialTimeframe={initialTimeframe}
                onEnableFetch={() => setFetchEnabled(true)}
                changeTime={selectedTimeframe}
                setChangeTime={({ start, end }) =>
                  setSelectedTimeframe({ timeframeStart: start, timeframeEnd: end })
                }
              />
            </Grid2>
            <Grid2>
              <EndDatePicker
                selectedTimeframe={selectedTimeframe}
                initialTimeframe={initialTimeframe}
                onEnableFetch={() => setFetchEnabled(true)}
                changeTime={selectedTimeframe}
                setChangeTime={({ start, end }) =>
                  setSelectedTimeframe({ timeframeStart: start, timeframeEnd: end })
                }
              />
            </Grid2>
          </Grid2>

          <IconButton
            sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}
            onClick={() => setFilterSlideOpen(true)}
            disabled={isReportFetching || !fleetRows?.length || !capacityData}
            data-testid="fleet-report-filter-btn"
            color={isFilterApplied ? "primary" : "inherit"}
          >
            <FilterAlt />
          </IconButton>

          {showResetButton && (
            <Tooltip title={t("Reset")}>
              <IconButton
                sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}
                onClick={handleReset}
                disabled={isReportFetching}
                data-testid="fleet-report-reset-btn"
              >
                <Replay />
              </IconButton>
            </Tooltip>
          )}

          {fetchEnabled && (
            <Button
              data-testid="fleet-report-calculate-btn"
              onClick={handleCalculate}
              disabled={isReportFetching}
            >
              {t("Calculate")}
              {isReportFetching ? (
                <DefaultSpinner
                  style={{ padding: 0, paddingLeft: "10px" }}
                  color="white"
                  size={20}
                />
              ) : null}
            </Button>
          )}
        </Grid2>

        <Grid2 container sx={{ width: "100%" }} data-testid="fleet-report-content">
          {!isReportFetching && entitiesData?.vehicles?.length === 0 && (
            <Grid2 size={12} sx={{ py: 2, pl: 2 }} data-testid="fleet-report-empty">
              <Alert severity="info" variant="standard">
                {t("_fleetReportNoVehiclesInDateRange")}
              </Alert>
            </Grid2>
          )}

          {!isReportFetching && (fleetRows?.length ?? 0) > 0 && capacityData && (
            <>
              {Array.isArray(fleetRows) && (
                <Grid2 size={12} sx={{ width: "100%" }} data-testid="fleet-report-table-grid">
                  <FleetCapacityReportTable
                    vehicles={fleetRows}
                    capacityPerformancePerWorkplace={
                      capacityData.capacityPerformancePerWorkplace ?? []
                    }
                    selectedVehicleIds={selectedVehicleIds}
                    onSelectionChange={ids => {
                      setSelectedVehicleIds(ids);
                      if (ids.length > 0) setFetchEnabled(true);
                    }}
                  />
                </Grid2>
              )}

              <Grid2 size={12} sx={{ width: "100%", mt: 2 }} data-testid="fleet-report-chips">
                <SvgReportChipGroup
                  workplaceBookingEnabled={workplaceBookingEnabled}
                  averageUsers={capacityData.distinctTotalUsers ?? []}
                  isFleetReport={true}
                  selectedVehicleCount={capacityData.uniqueInventoryIds?.length ?? 0}
                  avg={formatCapacityPercent(capacityData.averageCapacityPerformancePerDay)}
                  dev={formatCapacityPercent(capacityData.dayStandardDeviation)}
                  currentFloorCapaUniqueIds={capacityData.uniqueInventoryIds}
                  numWorkplaces={0}
                  numParkings={0}
                  numElectricchargings={0}
                  numConferences={0}
                />
              </Grid2>

              <Grid2 size={12} sx={{ width: "100%" }} data-testid="fleet-charts-bottom-grid">
                <Fade mountOnEnter unmountOnExit in>
                  <Grid2 container sx={{ width: "100%" }}>
                    <Grid2
                      size={{ sm: 12, md: 6 }}
                      p={isMobile ? 0 : "0 4px 0 0"}
                      sx={{ width: "100%" }}
                    >
                      <WeekdayBars data={capacityEntityData} />
                    </Grid2>
                    <Grid2
                      size={{ sm: 12, md: 6 }}
                      p={isMobile ? 0 : "0 0 0 4px"}
                      sx={{ width: "100%" }}
                    >
                      <CapacityPerHourReport capacityObjects={capacityObj} />
                    </Grid2>
                  </Grid2>
                </Fade>
              </Grid2>

              <Grid2
                size={12}
                sx={{ width: "100%", mt: 2, mb: 1 }}
                data-testid="fleet-area-charts-grid"
              >
                <AreaChartsReport capacityObjects={capacityObj} width="100%" />
              </Grid2>
            </>
          )}
        </Grid2>
      </Box>

      <FleetFilterSlideout
        isOpen={filterSlideOpen}
        onClose={() => setFilterSlideOpen(false)}
        filter={{
          timeframe: selectedTimeframe,
          weekdays: selectedWeekdays,
          departmentIds: selectedDepartmentIds
        }}
        onApply={({ departmentIds, weekdays }: FleetFilterApplyPayload) =>
          handleFilterCalculate({ departmentIds, weekdays }, false)
        }
        onReset={() => {
          handleFilterCalculate({ departmentIds: [], weekdays: initialWeekdays }, true);
        }}
      />
    </>
  );
}
